/**
 * Attachment Utilities
 *
 * Comprehensive utility functions for handling file attachments.
 * Includes conversion, validation, and formatting helpers.
 */

import {
  AttachmentSource,
  AttachmentConversionResult,
  FileValidationResult,
  FileCategory,
  FILE_EXTENSION_MAP,
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_MB,
  ChatwootAttachment,
  AttachmentFailureReason,
} from "../types/attachment.types";
import { getUserToken } from "@/services/storage-data";
import config from "@/config";

/**
 * Format file size for human-readable display
 * @param bytes - File size in bytes
 * @returns Formatted string (e.g., "1.5 MB")
 */
export const formatFileSize = (bytes?: number): string => {
  if (bytes === undefined || bytes === null || bytes < 0) return "";
  if (bytes === 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

/**
 * Extract file name from URL
 * Handles encoded URLs and various path formats
 * @param url - File URL
 * @returns Extracted file name or fallback
 */
export const getFileNameFromUrl = (url: string): string => {
  if (!url || typeof url !== "string") return "File";

  try {
    // Try to decode URI components
    const decoded = decodeURIComponent(url);
    // Extract the last path segment
    const urlObj = new URL(decoded, "http://placeholder.com");
    const pathname = urlObj.pathname;
    const segments = pathname.split("/").filter(Boolean);
    const lastSegment = segments[segments.length - 1];

    // Clean up query parameters if present
    const fileName = lastSegment?.split("?")[0] || "File";
    return fileName || "File";
  } catch {
    // Fallback: simple split on /
    try {
      const parts = url.split("/");
      const lastPart = parts[parts.length - 1]?.split("?")[0];
      return lastPart || "File";
    } catch {
      return "File";
    }
  }
};

const HUB_ATTACHMENT_PATH = /^\/api\/accounts\/messages\/[^/]+\/attachments\/\d+$/;

const messagingServerBase = (): string =>
  (config.messaging_server || "http://localhost:8080").replace(/\/$/, "");

/**
 * True for the messaging-hub attachment endpoint (relative, or absolute on the
 * messaging server). Only these URLs receive the user's bearer token.
 */
export const isHubAttachmentUrl = (url: string | null | undefined): boolean => {
  const raw = (url || "").trim();
  if (HUB_ATTACHMENT_PATH.test(raw)) return true;
  if (!/^https?:\/\//i.test(raw)) return false;
  try {
    const parsed = new URL(raw);
    return (
      parsed.origin === new URL(messagingServerBase()).origin &&
      HUB_ATTACHMENT_PATH.test(parsed.pathname)
    );
  } catch {
    return false;
  }
};

const FAILURE_REASONS: ReadonlySet<string> = new Set<AttachmentFailureReason>([
  "not_found",
  "expired",
  "not_stored",
  "unsupported_type",
  "ambiguous",
  "provider_auth",
  "provider_unavailable",
  "access_denied",
  "unknown",
]);

const toFailureReason = (value: unknown): AttachmentFailureReason =>
  typeof value === "string" && FAILURE_REASONS.has(value)
    ? (value as AttachmentFailureReason)
    : "unknown";

export class AttachmentDownloadError extends Error {
  readonly reason: AttachmentFailureReason;

  constructor(reason: AttachmentFailureReason) {
    super(reason);
    this.name = "AttachmentDownloadError";
    this.reason = reason;
  }
}

export const attachmentFailureReason = (error: unknown): AttachmentFailureReason =>
  error instanceof AttachmentDownloadError ? error.reason : "unknown";

/** Reason known before any request (no bytes and no hub source), or null when downloadable. */
export const getKnownUnavailableReason = (
  attachment: Pick<ChatwootAttachment, "data_url" | "unavailable_reason">
): AttachmentFailureReason | null => {
  if (typeof attachment.data_url === "string" && attachment.data_url.trim()) return null;
  return toFailureReason(attachment.unavailable_reason);
};

const ATTACHMENT_FAILURE_KEYS: Record<AttachmentFailureReason, string> = {
  not_found: "frontdesk.attachments.notFound",
  expired: "frontdesk.attachments.expired",
  not_stored: "frontdesk.attachments.notStored",
  unsupported_type: "frontdesk.attachments.unsupported",
  ambiguous: "frontdesk.attachments.ambiguous",
  provider_auth: "frontdesk.attachments.providerAuth",
  provider_unavailable: "frontdesk.attachments.providerUnavailable",
  access_denied: "frontdesk.attachments.accessDenied",
  unknown: "frontdesk.attachments.unavailable",
};

export const attachmentFailureMessageKey = (reason: AttachmentFailureReason): string =>
  ATTACHMENT_FAILURE_KEYS[reason];

async function readFailureReason(response: Response): Promise<AttachmentFailureReason> {
  if (response.status === 401 || response.status === 403) return "access_denied";
  try {
    const body = (await response.json()) as { reason?: unknown };
    const reason = toFailureReason(body?.reason);
    if (reason !== "unknown") return reason;
  } catch {
    // Non-JSON error body
  }
  return response.status >= 500 ? "provider_unavailable" : "unknown";
}

/** Fetch attachment bytes from the messaging hub with the user's bearer token. */
export async function fetchHubAttachmentBlob(url: string, signal?: AbortSignal): Promise<Blob> {
  if (!isHubAttachmentUrl(url)) throw new AttachmentDownloadError("unknown");
  const token = getUserToken();
  if (!token) throw new AttachmentDownloadError("access_denied");

  const raw = url.trim();
  const endpoint = raw.startsWith("/") ? `${messagingServerBase()}${raw}` : raw;
  let response: Response;
  try {
    response = await fetch(endpoint, {
      headers: { Authorization: `Bearer ${token}` },
      signal,
    });
  } catch (error) {
    if ((error as Error)?.name === "AbortError") throw error;
    throw new AttachmentDownloadError("provider_unavailable");
  }
  if (!response.ok) throw new AttachmentDownloadError(await readFailureReason(response));
  return response.blob();
}

/** Types the browser may render in a new tab without running script in our origin. */
const SAFE_TO_OPEN_MIME =
  /^(application\/pdf|text\/plain|image\/(png|jpe?g|gif|webp|bmp)|audio\/[\w.+-]+|video\/[\w.+-]+)$/i;

const normalizeMime = (value: string | null | undefined): string =>
  (value || "").split(";")[0].trim().toLowerCase();

const dataUrlToBlob = (dataUrl: string, fallbackMime: string): Blob => {
  const comma = dataUrl.indexOf(",");
  if (comma < 0) throw new AttachmentDownloadError("unknown");
  const header = dataUrl.slice(0, comma);
  const payload = dataUrl.slice(comma + 1);
  const mime = normalizeMime(fallbackMime || header.match(/^data:([^;,]+)/i)?.[1]);
  let bytes: Uint8Array;
  if (/;base64/i.test(header)) {
    const binary = atob(payload);
    bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
  } else {
    bytes = new TextEncoder().encode(decodeURIComponent(payload));
  }
  return new Blob([bytes], { type: mime || "application/octet-stream" });
};

/**
 * Open (safe types only) or download a blob. Unsafe types such as HTML/SVG are
 * always downloaded: a blob URL runs in our origin.
 */
const deliverBlob = (
  blob: Blob,
  fileName: string,
  mode: "open" | "download",
  mime: string,
  pendingWindow: Window | null
): void => {
  const type = normalizeMime(mime || blob.type);
  const canOpen = mode === "open" && SAFE_TO_OPEN_MIME.test(type);
  const typed = new Blob([blob], { type: canOpen ? type : "application/octet-stream" });
  const objectUrl = URL.createObjectURL(typed);
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);

  if (canOpen && pendingWindow && !pendingWindow.closed) {
    pendingWindow.location.href = objectUrl;
    return;
  }
  pendingWindow?.close();

  const link = document.createElement("a");
  link.href = objectUrl;
  if (canOpen) {
    link.target = "_blank";
    link.rel = "noopener noreferrer";
  } else {
    link.download = fileName;
  }
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const attachmentDisplayName = (
  attachment: Pick<ChatwootAttachment, "data_url" | "filename" | "extension">
): string => {
  const explicit = typeof attachment.filename === "string" ? attachment.filename.trim() : "";
  if (explicit) return explicit;
  const url = attachment.data_url || "";
  if (url && !url.startsWith("data:") && !isHubAttachmentUrl(url)) {
    return getFileNameFromUrl(url);
  }
  return `attachment${attachment.extension ? `.${attachment.extension}` : ""}`;
};

/**
 * Open or download an attachment: inline `data:` bytes, the authenticated hub
 * endpoint (provider-backed files), or a public URL. Never navigates the
 * browser to a relative path on the gomake-web origin.
 */
export const openOrDownloadAttachment = async (
  attachment: Pick<
    ChatwootAttachment,
    "data_url" | "filename" | "extension" | "content_type" | "unavailable_reason"
  >,
  mode: "open" | "download" = "download"
): Promise<void> => {
  const known = getKnownUnavailableReason(attachment);
  if (known) throw new AttachmentDownloadError(known);

  const dataUrl = attachment.data_url.trim();
  const fileName = attachmentDisplayName(attachment);

  if (dataUrl.startsWith("data:")) {
    deliverBlob(
      dataUrlToBlob(dataUrl, attachment.content_type || ""),
      fileName,
      mode,
      attachment.content_type || "",
      null
    );
    return;
  }

  if (isHubAttachmentUrl(dataUrl)) {
    // Opened synchronously so the browser keeps the user gesture across the fetch.
    const pendingWindow =
      mode === "open" && SAFE_TO_OPEN_MIME.test(normalizeMime(attachment.content_type))
        ? window.open("", "_blank")
        : null;
    if (pendingWindow) pendingWindow.opener = null;
    try {
      const blob = await fetchHubAttachmentBlob(dataUrl);
      deliverBlob(blob, fileName, mode, attachment.content_type || blob.type, pendingWindow);
    } catch (error) {
      pendingWindow?.close();
      throw error;
    }
    return;
  }

  if (!/^https?:\/\//i.test(dataUrl)) throw new AttachmentDownloadError("unknown");

  const link = document.createElement("a");
  link.href = dataUrl;
  if (mode === "download") {
    link.download = fileName;
  } else {
    link.target = "_blank";
    link.rel = "noopener noreferrer";
  }
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Get file extension from URL or explicit extension
 * @param url - File URL
 * @param extension - Optional explicit extension
 * @returns Lowercase extension without dot
 */
export const getFileExtension = (url: string, extension?: string | null): string => {
  if (extension) {
    return extension.toLowerCase().replace(/^\./, "");
  }

  const fileName = getFileNameFromUrl(url);
  const parts = fileName.split(".");
  if (parts.length > 1) {
    return parts[parts.length - 1].toLowerCase();
  }
  return "";
};

/**
 * Get file category from file type and extension
 * @param fileType - Chatwoot file_type
 * @param extension - File extension
 * @returns File category for icon mapping
 */
export const getFileCategory = (
  fileType: string,
  extension: string
): FileCategory => {
  // Check explicit file type first
  if (fileType === "audio") return "audio";
  if (fileType === "video") return "video";
  if (fileType === "image") return "image";
  if (fileType === "location") return "location";
  if (fileType === "contact") return "contact";

  // Check extension mapping
  const normalizedExt = extension.toLowerCase();
  return FILE_EXTENSION_MAP[normalizedExt] || "unknown";
};

/**
 * Validate file size
 * @param size - File size in bytes
 * @returns Validation result
 */
export const validateFileSize = (size: number): FileValidationResult => {
  if (size === 0) {
    return {
      valid: false,
      error: "File is empty",
      errorCode: "EMPTY_FILE",
    };
  }

  if (size > MAX_FILE_SIZE_BYTES) {
    return {
      valid: false,
      error: `File size exceeds ${MAX_FILE_SIZE_MB}MB limit`,
      errorCode: "SIZE_EXCEEDED",
    };
  }

  return { valid: true };
};

/**
 * Check if file size is within limit
 * @param sizeInBytes - File size in bytes
 * @returns True if within limit
 */
export const isFileSizeValid = (sizeInBytes: number): boolean => {
  return sizeInBytes > 0 && sizeInBytes <= MAX_FILE_SIZE_BYTES;
};

/**
 * Convert file size in bytes to megabytes
 * @param bytes - Size in bytes
 * @returns Size in MB
 */
export const bytesToMB = (bytes: number): number => {
  return bytes / (1024 * 1024);
};

/**
 * Convert base64 string to File object
 * @param base64 - Base64 encoded content
 * @param filename - File name
 * @param mimeType - MIME type
 * @returns File object or null on error
 */
export const base64ToFile = (
  base64: string,
  filename: string,
  mimeType: string
): File | null => {
  if (!base64 || typeof base64 !== "string") return null;

  try {
    // Remove data URL prefix if present
    const cleanBase64 = base64.includes(",")
      ? base64.split(",")[1]
      : base64;

    const byteCharacters = window.atob(cleanBase64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });
    return new File([blob], filename, { type: mimeType });
  } catch (error) {
    console.error("[attachment.utils] Failed to convert base64 to file:", error);
    return null;
  }
};

/**
 * Fetch file from URL and convert to File object
 * Handles CORS and network errors gracefully
 * @param url - File URL
 * @param filename - File name
 * @param mimeType - Optional MIME type
 * @param timeout - Request timeout in ms (default: 30000)
 * @returns File object or null on error
 */
export const urlToFile = async (
  url: string,
  filename: string,
  mimeType?: string,
  timeout: number = 30000
): Promise<File | null> => {
  if (!url || typeof url !== "string") return null;

  try {
    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    const response = await fetch(url, {
      signal: controller.signal,
      // Don't include credentials to avoid CORS issues with external URLs
      credentials: "omit",
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const blob = await response.blob();

    // Use provided MIME type or fallback to response content-type
    const finalMimeType =
      mimeType || blob.type || "application/octet-stream";

    return new File([blob], filename, { type: finalMimeType });
  } catch (error) {
    if (error instanceof Error) {
      if (error.name === "AbortError") {
        console.error("[attachment.utils] URL fetch timeout:", url);
      } else {
        console.error("[attachment.utils] Failed to fetch URL:", url, error.message);
      }
    }
    return null;
  }
};

/**
 * MIME type mapping from extensions
 * Used when contentType is not provided
 */
const EXTENSION_TO_MIME: Record<string, string> = {
  // Documents
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  txt: "text/plain",
  rtf: "application/rtf",
  csv: "text/csv",
  json: "application/json",
  xml: "application/xml",
  md: "text/markdown",
  // Images
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  bmp: "image/bmp",
  svg: "image/svg+xml",
  // Audio
  mp3: "audio/mpeg",
  wav: "audio/wav",
  ogg: "audio/ogg",
  m4a: "audio/mp4",
  aac: "audio/aac",
  // Video
  mp4: "video/mp4",
  webm: "video/webm",
  mov: "video/quicktime",
  avi: "video/x-msvideo",
  // Archives
  zip: "application/zip",
  rar: "application/vnd.rar",
  "7z": "application/x-7z-compressed",
  tar: "application/x-tar",
  gz: "application/gzip",
};

/**
 * Get MIME type from file extension
 * @param extension - File extension (without dot)
 * @returns MIME type string
 */
export const getMimeTypeFromExtension = (extension: string): string => {
  const ext = extension.toLowerCase().replace(/^\./, "");
  return EXTENSION_TO_MIME[ext] || "application/octet-stream";
};

/**
 * Ensure filename has the correct extension
 * Extracts extension from URL if filename doesn't have one
 * @param filename - Original filename
 * @param url - URL to extract extension from (if needed)
 * @returns Filename with extension
 */
const ensureFilenameExtension = (filename: string, url?: string): { filename: string; extension: string } => {
  // Check if filename already has an extension
  const filenameParts = filename.split(".");
  if (filenameParts.length > 1 && filenameParts[filenameParts.length - 1].length <= 5) {
    // Filename has extension
    return {
      filename,
      extension: filenameParts[filenameParts.length - 1].toLowerCase(),
    };
  }

  // Try to extract extension from URL
  if (url) {
    try {
      const urlPath = new URL(url).pathname;
      const urlParts = urlPath.split(".");
      if (urlParts.length > 1) {
        const ext = urlParts[urlParts.length - 1].toLowerCase().split("?")[0];
        if (ext.length <= 5 && /^[a-z0-9]+$/.test(ext)) {
          return {
            filename: `${filename}.${ext}`,
            extension: ext,
          };
        }
      }
    } catch {
      // URL parsing failed, continue without extension
    }
  }

  // No extension found
  return { filename, extension: "" };
};

/**
 * Convert attachment source to File object
 * Handles base64, URL, and data URL formats
 * Automatically detects MIME type and ensures filename has extension
 * @param source - Attachment source data
 * @returns Conversion result with file or error
 */
export const convertAttachmentToFile = async (
  source: AttachmentSource
): Promise<AttachmentConversionResult> => {
  // Ensure filename has extension (extract from URL if needed)
  const { filename: correctedFilename, extension } = ensureFilenameExtension(
    source.filename,
    source.url
  );

  // Determine MIME type: use provided, detect from extension, or fallback
  let mimeType = source.contentType || source.mimeType;
  if (!mimeType || mimeType === "application/octet-stream") {
    if (extension) {
      mimeType = getMimeTypeFromExtension(extension);
      console.log(`[attachment.utils] Detected MIME type from extension: ${extension} -> ${mimeType}`);
    } else {
      mimeType = "application/octet-stream";
    }
  }

  console.log(`[attachment.utils] Converting: ${source.filename} -> ${correctedFilename} (${mimeType})`);

  const base64 = source.content || source.data;

  // Try base64 first
  if (base64) {
    const file = base64ToFile(base64, correctedFilename, mimeType);
    if (file) {
      return {
        success: true,
        file,
        source: "base64",
      };
    }
    return {
      success: false,
      file: null,
      error: "Failed to decode base64 content",
      source: "base64",
    };
  }

  // Try URL
  if (source.url) {
    const file = await urlToFile(source.url, correctedFilename, mimeType);
    if (file) {
      return {
        success: true,
        file,
        source: "url",
      };
    }
    return {
      success: false,
      file: null,
      error: "Failed to fetch attachment from URL",
      source: "url",
    };
  }

  // No valid source
  return {
    success: false,
    file: null,
    error: "No content, data, or url provided",
    source: "unknown",
  };
};

/**
 * Convert multiple attachments to files
 * Logs progress and failures
 * @param sources - Array of attachment sources
 * @returns Array of successfully converted files
 */
export const convertAttachmentsToFiles = async (
  sources: AttachmentSource[]
): Promise<File[]> => {
  if (!sources || sources.length === 0) return [];

  console.log(`[attachment.utils] Converting ${sources.length} attachments...`);

  const results = await Promise.all(
    sources.map((source, index) =>
      convertAttachmentToFile(source).then((result) => ({
        ...result,
        index,
        filename: source.filename,
      }))
    )
  );

  const files: File[] = [];
  const failures: string[] = [];

  results.forEach((result) => {
    if (result.success && result.file) {
      files.push(result.file);
    } else {
      failures.push(`${result.filename}: ${result.error}`);
    }
  });

  if (failures.length > 0) {
    console.warn(
      `[attachment.utils] ${failures.length} attachment(s) failed to convert:`,
      failures
    );
  }

  console.log(
    `[attachment.utils] Successfully converted ${files.length}/${sources.length} attachments`
  );

  return files;
};

/**
 * Image file extensions
 * Official Chatwoot: png, jpeg, gif, bmp, webp
 * @see https://developers.chatwoot.com/self-hosted/supported-features#outgoing-attachments-supported-file-types
 */
const IMAGE_EXTENSIONS = new Set([
  "png", "jpg", "jpeg", "gif", "bmp", "webp",
  "svg", "ico", "tiff", "tif" // Additional common formats
]);

/**
 * Video file extensions
 * Official Chatwoot: ogg, mp4, mov, webm, quicktime, avi
 * @see https://developers.chatwoot.com/self-hosted/supported-features#outgoing-attachments-supported-file-types
 */
const VIDEO_EXTENSIONS = new Set([
  "ogg", "mp4", "mov", "webm", "avi",
  "mkv", "wmv", "flv", "m4v", "ogv" // Additional common formats
]);

/**
 * Audio file extensions
 * Official Chatwoot: mp3, mpeg, opus, ogg, amr, aac, m4a, wav
 * @see https://developers.chatwoot.com/self-hosted/supported-features#outgoing-attachments-supported-file-types
 */
const AUDIO_EXTENSIONS = new Set([
  "mp3", "mpeg", "opus", "ogg", "amr", "aac", "m4a", "wav",
  "flac", "wma" // Additional common formats
]);

/**
 * Determine the actual media type of an attachment
 * Checks both file_type and extension for more accurate detection
 * @param attachment - Chatwoot attachment
 * @returns Media category
 */
export const getAttachmentMediaType = (
  attachment: ChatwootAttachment
): "image" | "video" | "audio" | "file" => {
  // First, trust explicit file_type if it's a media type
  if (attachment.file_type === "image") return "image";
  if (attachment.file_type === "video") return "video";
  if (attachment.file_type === "audio") return "audio";

  // For generic "file" type or unknown, check extension
  const name = typeof attachment.filename === "string" ? attachment.filename.trim() : "";
  const extension = getFileExtension(name || attachment.data_url, attachment.extension);
  
  if (IMAGE_EXTENSIONS.has(extension)) return "image";
  if (VIDEO_EXTENSIONS.has(extension)) return "video";
  if (AUDIO_EXTENSIONS.has(extension)) return "audio";

  return "file";
};

/**
 * Categorize attachments by type
 * Uses both file_type and extension for accurate categorization
 * @param attachments - Array of Chatwoot attachments
 * @returns Categorized attachments
 */
export const categorizeAttachments = (
  attachments: ChatwootAttachment[]
): {
  images: ChatwootAttachment[];
  videos: ChatwootAttachment[];
  audios: ChatwootAttachment[];
  files: ChatwootAttachment[];
} => {
  const images: ChatwootAttachment[] = [];
  const videos: ChatwootAttachment[] = [];
  const audios: ChatwootAttachment[] = [];
  const files: ChatwootAttachment[] = [];

  attachments.forEach((attachment) => {
    const mediaType = getAttachmentMediaType(attachment);
    
    switch (mediaType) {
      case "image":
        images.push(attachment);
        break;
      case "video":
        videos.push(attachment);
        break;
      case "audio":
        audios.push(attachment);
        break;
      default:
        files.push(attachment);
        break;
    }
  });

  return { images, videos, audios, files };
};

/**
 * Check if attachment is previewable (image or video)
 * Uses smart detection with both file_type and extension
 * @param attachment - Chatwoot attachment
 * @returns True if can be previewed in modal
 */
export const isPreviewable = (attachment: ChatwootAttachment): boolean => {
  const mediaType = getAttachmentMediaType(attachment);
  return mediaType === "image" || mediaType === "video";
};

/**
 * Get preview type for modal
 * Uses smart detection with both file_type and extension
 * @param attachment - Chatwoot attachment
 * @returns "image" | "video" | null
 */
export const getPreviewType = (
  attachment: ChatwootAttachment
): "image" | "video" | null => {
  const mediaType = getAttachmentMediaType(attachment);
  if (mediaType === "image") return "image";
  if (mediaType === "video") return "video";
  return null;
};

/**
 * Generate a unique temporary ID for optimistic updates
 * @returns Unique string ID
 */
export const generateTempId = (): string => {
  return `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Resolve a renderable URL for image/video/audio attachments.
 * - data: / blob: / public http(s) → use as-is
 * - messaging-hub attachment endpoint → fetch with JWT → blob URL (caller revokes)
 */
export async function resolvePlayableAttachmentUrl(
  attachment: Pick<ChatwootAttachment, "data_url" | "unavailable_reason">,
  signal?: AbortSignal
): Promise<{ url: string; revoke?: () => void }> {
  const known = getKnownUnavailableReason(attachment);
  if (known) throw new AttachmentDownloadError(known);
  const raw = attachment.data_url.trim();

  if (raw.startsWith("data:") || raw.startsWith("blob:")) {
    return { url: raw };
  }
  if (isHubAttachmentUrl(raw)) {
    const blob = await fetchHubAttachmentBlob(raw, signal);
    const objectUrl = URL.createObjectURL(blob);
    return {
      url: objectUrl,
      revoke: () => URL.revokeObjectURL(objectUrl),
    };
  }
  if (/^https?:\/\//i.test(raw)) {
    return { url: raw };
  }
  throw new AttachmentDownloadError("unknown");
}

